import React, {
    useRef,
    useState,
    useEffect,
    useCallback,
    ReactNode,
} from 'react';
import { Box, IconButton, makeStyles } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

interface HorizontalScrollableFilterProps {
    children: ReactNode;
    scrollAmount?: number;
    arrowSize?: 'small' | 'medium';
    containerClassName?: string;
    contentClassName?: string;
    gap?: number;
}

const useStyles = makeStyles((theme) => ({
    wrapper: {
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
    },
    scrollContainer: {
        display: 'flex',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'smooth',
        width: '100%',
        // Hide scrollbar for all browsers
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE/Edge
        '&::-webkit-scrollbar': {
            display: 'none', // Chrome, Safari, Opera
        },
    },
    contentContainer: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
    },
    arrowButton: {
        position: 'absolute',
        zIndex: 10,
        backgroundColor: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            backgroundColor: '#f5f5f5',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        },
        '&:disabled': {
            opacity: 0,
            pointerEvents: 'none',
        },
    },
    leftArrow: {
        left: 0,
        transform: 'translateX(-50%)',
    },
    rightArrow: {
        right: 0,
        transform: 'translateX(50%)',
    },
    arrowIcon: {
        color: '#14112D',
    },
}));

const HorizontalScrollableFilter: React.FC<HorizontalScrollableFilterProps> = ({
    children,
    scrollAmount = 200,
    arrowSize = 'medium',
    containerClassName = '',
    contentClassName = '',
    gap = 8,
}) => {
    const classes = useStyles();
    const scrollContainerRef: any = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);

    // Check if content overflows and update arrow visibility
    const checkOverflow = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;
        const hasOverflow = scrollWidth > clientWidth;

        setIsOverflowing(hasOverflow);
        setShowLeftArrow(hasOverflow && scrollLeft > 1);
        setShowRightArrow(
            hasOverflow && scrollLeft < scrollWidth - clientWidth - 1,
        );
    }, []);

    // Scroll handlers
    const handleScrollLeft = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.scrollBy({
            left: -scrollAmount,
            behavior: 'smooth',
        });
    }, [scrollAmount]);

    const handleScrollRight = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.scrollBy({
            left: scrollAmount,
            behavior: 'smooth',
        });
    }, [scrollAmount]);

    // Handle scroll event
    const handleScroll = useCallback(() => {
        checkOverflow();
    }, [checkOverflow]);

    // Set up ResizeObserver to detect size changes
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        // Initial check
        checkOverflow();

        // Observe resize
        const resizeObserver = new ResizeObserver(() => {
            checkOverflow();
        });

        resizeObserver.observe(container);

        // Also observe children changes
        const mutationObserver = new MutationObserver(() => {
            checkOverflow();
        });

        mutationObserver.observe(container, {
            childList: true,
            subtree: true,
        });

        // Cleanup
        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [checkOverflow]);

    // Re-check on window resize
    useEffect(() => {
        const handleResize = () => {
            checkOverflow();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [checkOverflow]);

    // Check overflow when children change
    useEffect(() => {
        // Small delay to ensure DOM is updated
        const timer = setTimeout(() => {
            checkOverflow();
        }, 100);

        return () => clearTimeout(timer);
    }, [children, checkOverflow]);

    return (
        <Box className={`${classes.wrapper} ${containerClassName}`}>
            {/* Left Arrow */}
            {isOverflowing && showLeftArrow && (
                <IconButton
                    className={`${classes.arrowButton} ${classes.leftArrow}`}
                    onClick={handleScrollLeft}
                    size={arrowSize}
                    aria-label="Scroll left"
                >
                    <ChevronLeftIcon className={classes.arrowIcon} />
                </IconButton>
            )}

            {/* Scrollable Content */}
            <Box
                component="div"
                className={classes.scrollContainer}
                onScroll={handleScroll}
                {...{
                    ref: scrollContainerRef,
                }}
            >
                <Box
                    className={`${classes.contentContainer} ${contentClassName}`}
                    style={{ gap: `${gap}px` }}
                >
                    {children}
                </Box>
            </Box>

            {/* Right Arrow */}
            {isOverflowing && showRightArrow && (
                <IconButton
                    className={`${classes.arrowButton} ${classes.rightArrow}`}
                    onClick={handleScrollRight}
                    size={arrowSize}
                    aria-label="Scroll right"
                >
                    <ChevronRightIcon className={classes.arrowIcon} />
                </IconButton>
            )}
        </Box>
    );
};

export default HorizontalScrollableFilter;
